import React from "react";

import Box from "@mui/material/Box";
import {
  Alert,
  Paper,
  Stack,
  TextField,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Bounce, toast } from "react-toastify";
import * as Yup from "yup";
import { Formik } from "formik";

import AppLayout from "../common/AppLayout";
import { organizationsService } from "./services";
import CustomButton from "../common/Button";
import { useUserState } from "../../store";
import { RoleName } from "../common/enums/RoleName";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </Box>
  );
}

export default function AccountView() {
  const { user } = useUserState();
  const [tabValue, setTabValue] = React.useState(0);

  const isViewer = () => {
    if (!user || !Array.isArray(user.roles)) {
      return true;
    }
    return !user.roles.some(
      (role) => role === RoleName.ADMIN || role === RoleName.EDITOR,
    );
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const { data, isSuccess } = useQuery({
    queryKey: ["organization"],
    queryFn: () => organizationsService.fetchOrganizationDetails(),
  });

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationKey: ["update-project"],
    mutationFn: (value: string) =>
      organizationsService.updateOrganization(value),
  });

  const handleSave = async (params: { organization_name: string }) => {
    await toast.promise(mutateAsync(params.organization_name), {
      success: {
        toastId: `${params.organization_name}-success`,
        render() {
          return "Updated!";
        },
        type: "success",
        theme: "colored",
        autoClose: 2000,
        transition: Bounce,
        delay: 500,
      },
    });
  };

  return (
    <AppLayout pageName="Account Settings" maxWidth={900}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: "text.primary", mb: 2 }}
      >
        Account
      </Typography>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "#fff",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "grey.50",
            px: 1,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleChange}
            aria-label="Account settings tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "0.9rem",
                fontWeight: 600,
                minHeight: 52,
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "#2563eb",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#2563eb",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            <Tab
              icon={<BusinessIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label="Organization"
              sx={{ px: 2 }}
            />
          </Tabs>
        </Box>

        <CustomTabPanel value={tabValue} index={0}>
          <Stack spacing={3} sx={{ maxWidth: 560, mx: "auto" }}>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
              >
                Organization settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your organization name. Only admins and editors can make
                changes.
              </Typography>
            </Box>

            {isSuccess && (
              <Formik
                initialValues={{ organization_name: data?.name ?? "" }}
                validationSchema={Yup.object({
                  organization_name: Yup.string().required(
                    "Organization name is required",
                  ),
                })}
                onSubmit={handleSave}
              >
                {(formik) => (
                  <form onSubmit={formik.handleSubmit}>
                    <Stack spacing={2.5}>
                      {isError ? (
                        <Alert
                          severity="error"
                          sx={{
                            borderRadius: "12px",
                            border: "1px solid #fca5a5",
                          }}
                        >
                          {error.message}
                        </Alert>
                      ) : null}

                      <TextField
                        label="Organization name"
                        fullWidth
                        error={!!formik.errors.organization_name}
                        helperText={formik.errors.organization_name}
                        disabled={isViewer()}
                        {...formik.getFieldProps("organization_name")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#2563eb",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#2563eb",
                          },
                        }}
                      />

                      <CustomButton
                        label="Save changes"
                        type="submit"
                        variant="contained"
                        disabled={isPending || isViewer()}
                        sx={{
                          alignSelf: "flex-start",
                          borderRadius: "8px",
                          px: 2.5,
                          py: 1,
                          fontSize: "0.875rem",
                          textTransform: "none",
                          fontWeight: 600,
                          backgroundColor: "#2563eb",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#1d4ed8",
                            boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
                          },
                          "&:disabled": {
                            backgroundColor: "action.disabledBackground",
                            color: "text.disabled",
                            boxShadow: "none",
                          },
                        }}
                      />
                    </Stack>
                  </form>
                )}
              </Formik>
            )}
          </Stack>
        </CustomTabPanel>
      </Paper>
    </AppLayout>
  );
}
